"use client";

import React, { useState, useEffect, useRef } from "react";
import { useTranslations, useLocale } from "next-intl";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { getCountries, getUserCountry, type Country } from "@/lib/services/country-service";
import Image from "next/image";

interface CountryWithCode extends Country {
  phoneCode?: string; // e.g., "+20", "+1", "+93"
}

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onPhoneChange?: (fullPhoneNumber: string, country: CountryWithCode) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  initialCountry?: CountryWithCode | null;
}

// Helper to extract phone code from idd structure
function getPhoneCode(country: any): string {
  if (country.phoneCode) {
    return country.phoneCode;
  }
  
  // Try to get from idd structure
  if (country.idd) {
    const idd = country.idd;
    if (idd.root && idd.suffixes && Array.isArray(idd.suffixes) && idd.suffixes.length > 0) {
      return `${idd.root}${idd.suffixes[0]}`;
    }
    if (idd.root) {
      return idd.root;
    }
  }
  
  return "+1"; // Default to US
}

export default function PhoneInput({
  value,
  onChange,
  onPhoneChange,
  placeholder,
  label,
  error,
  disabled,
  className,
  initialCountry,
}: PhoneInputProps) {
  const t = useTranslations("general");
  const locale = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [countries, setCountries] = useState<CountryWithCode[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<CountryWithCode[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<CountryWithCode | null>(null);
  const [isDetecting, setIsDetecting] = useState(!initialCountry);
  const [phoneNumberWithoutCode, setPhoneNumberWithoutCode] = useState("");
  const phoneInputRef = useRef<HTMLInputElement>(null);

  // Load countries and initialize
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      try {
        const countriesList = await getCountries();
        // Add phone codes to countries
        const countriesWithCodes: CountryWithCode[] = countriesList.map((country: any) => ({
          ...country,
          phoneCode: getPhoneCode(country),
        }));
        
        setCountries(countriesWithCodes);
        setFilteredCountries(countriesWithCodes);

        // Set initial country
        let country: CountryWithCode | null = null;
        if (initialCountry) {
          country = { ...initialCountry, phoneCode: getPhoneCode(initialCountry) };
        } else {
          setIsDetecting(true);
          const detected = await getUserCountry();
          if (detected) {
            country = { ...detected, phoneCode: getPhoneCode(detected) };
          }
          setIsDetecting(false);
        }
        
        if (country) {
          setSelectedCountry(country);
        } else {
          // Default to Egypt (target country for first phase)
          const egyptCountry = countriesWithCodes.find(c => c.name.common === "Egypt");
          if (egyptCountry) {
            setSelectedCountry(egyptCountry);
            // Notify parent of initial country selection (even if phone is empty)
            if (onPhoneChange) {
              const phoneCode = getPhoneCode(egyptCountry);
              onPhoneChange(phoneCode || "", egyptCountry);
            }
          } else if (countriesWithCodes.length > 0) {
            // Fallback to first country if Egypt not found
            setSelectedCountry(countriesWithCodes[0]);
            if (onPhoneChange) {
              const phoneCode = getPhoneCode(countriesWithCodes[0]);
              onPhoneChange(phoneCode || "", countriesWithCodes[0]);
            }
          }
        }

        // Extract phone number without country code if value contains country code
        if (value) {
          const phoneCode = country?.phoneCode || getPhoneCode(country || countriesWithCodes[0]);
          if (value.startsWith(phoneCode)) {
            setPhoneNumberWithoutCode(value.substring(phoneCode.length));
          } else {
            setPhoneNumberWithoutCode(value);
          }
        }
      } catch (error) {
        console.error("Error initializing phone input:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [initialCountry]);

  // Filter countries based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCountries(countries);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = countries.filter((country) =>
      country.name.common.toLowerCase().includes(query) ||
      country.phoneCode?.includes(query) ||
      country.name.common.toLowerCase().includes(query)
    );
    setFilteredCountries(filtered);
  }, [searchQuery, countries]);

  // Sync phone number when value prop changes externally
  useEffect(() => {
    if (!value) {
      setPhoneNumberWithoutCode("");
      return;
    }

    // If we have a selected country, try to extract the number part
    if (selectedCountry) {
      const phoneCode = selectedCountry.phoneCode || getPhoneCode(selectedCountry);
      if (value.startsWith(phoneCode)) {
        setPhoneNumberWithoutCode(value.substring(phoneCode.length));
      } else if (!phoneNumberWithoutCode) {
        // If value doesn't start with code, use it as-is
        setPhoneNumberWithoutCode(value);
      }
    } else if (!phoneNumberWithoutCode) {
      // No country selected yet, use value as-is
      setPhoneNumberWithoutCode(value);
    }
  }, [value, selectedCountry]);

  const handleSelectCountry = (country: CountryWithCode) => {
    setSelectedCountry(country);
    setIsOpen(false);
    setSearchQuery("");
    
    // Notify parent of full phone number change
    if (onPhoneChange) {
      const phoneCode = country.phoneCode || getPhoneCode(country);
      const fullNumber = phoneCode ? `${phoneCode}${phoneNumberWithoutCode}` : phoneNumberWithoutCode;
      onPhoneChange(fullNumber, country);
      onChange(fullNumber);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setPhoneNumberWithoutCode(newValue);
    
    // Notify parent of full phone number change
    if (selectedCountry) {
      const phoneCode = selectedCountry.phoneCode || getPhoneCode(selectedCountry);
      const fullNumber = phoneCode ? `${phoneCode}${newValue}` : newValue;
      onChange(fullNumber);
      if (onPhoneChange) {
        onPhoneChange(fullNumber, selectedCountry);
      }
    } else {
      onChange(newValue);
    }
  };

  const displayCode = selectedCountry?.phoneCode || (isDetecting ? "" : "+1");
  const displayFlag = selectedCountry?.flags?.png;

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <label className="text-sm font-medium text-primary-foreground font-poppins">
          {label}
        </label>
      )}
      <div className="relative flex items-center">
        {locale === "ar" ? (
          <>
            {/* Arabic: Code picker on left, Phone input on right */}
            <button
              type="button"
              onClick={() => !disabled && setIsOpen(true)}
              disabled={disabled || isLoading || isDetecting}
              className={cn(
                "h-12 bg-black/50 border border-white/20 text-primary-foreground hover:bg-black/70 hover:text-white px-3 sm:px-4 flex items-center gap-2 min-w-[100px] sm:min-w-[120px] transition-colors rounded-r-full rounded-l-none border-r-0",
                error && "border-red-500",
                (disabled || isLoading || isDetecting) && "opacity-50 cursor-not-allowed"
              )}
            >
              {isLoading || isDetecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {displayFlag ? (
                    <Image
                      src={displayFlag}
                      alt={selectedCountry?.name.common || "Country"}
                      width={20}
                      height={15}
                      className="rounded-sm object-cover"
                    />
                  ) : (
                    <div className="w-5 h-4 bg-white/20 rounded-sm" />
                  )}
                  <span className="text-sm font-medium font-poppins text-primary-foreground hover:text-white transition-colors">
                    {displayCode}
                  </span>
                  <ChevronDown className="w-4 h-4 opacity-70 text-primary-foreground hover:text-white transition-colors" />
                </>
              )}
            </button>
            <Input
              ref={phoneInputRef}
              type="tel"
              value={phoneNumberWithoutCode}
              onChange={handlePhoneChange}
              placeholder={placeholder || "Enter your phone number"}
              disabled={disabled}
              className={cn(
                "h-12 bg-black/50 border-white/20 text-primary-foreground placeholder:text-primary-foreground/50 px-6 py-3 focus:border-primary rounded-l-full rounded-r-none text-right",
                error && "border-red-500"
              )}
              dir="rtl"
            />
          </>
        ) : (
          <>
            {/* English: Code picker on left, Phone input on right */}
            <button
              type="button"
              onClick={() => !disabled && setIsOpen(true)}
              disabled={disabled || isLoading || isDetecting}
              className={cn(
                "h-12 bg-black/50 border border-white/20 text-primary-foreground hover:bg-black/70 hover:text-white px-3 sm:px-4 flex items-center gap-2 min-w-[100px] sm:min-w-[120px] transition-colors rounded-l-full rounded-r-none border-r-0",
                error && "border-red-500",
                (disabled || isLoading || isDetecting) && "opacity-50 cursor-not-allowed"
              )}
            >
              {isLoading || isDetecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {displayFlag ? (
                    <Image
                      src={displayFlag}
                      alt={selectedCountry?.name.common || "Country"}
                      width={20}
                      height={15}
                      className="rounded-sm object-cover"
                    />
                  ) : (
                    <div className="w-5 h-4 bg-white/20 rounded-sm" />
                  )}
                  <span className="text-sm font-medium font-poppins text-primary-foreground hover:text-white transition-colors">
                    {displayCode}
                  </span>
                  <ChevronDown className="w-4 h-4 opacity-70 text-primary-foreground hover:text-white transition-colors" />
                </>
              )}
            </button>
            <Input
              ref={phoneInputRef}
              type="tel"
              value={phoneNumberWithoutCode}
              onChange={handlePhoneChange}
              placeholder={placeholder || "Enter your phone number"}
              disabled={disabled}
              className={cn(
                "h-12 bg-black/50 border-white/20 text-primary-foreground placeholder:text-primary-foreground/50 px-6 py-3 focus:border-primary rounded-r-full rounded-l-none text-left",
                error && "border-red-500"
              )}
              dir="ltr"
            />
          </>
        )}
      </div>

      {/* Country Picker Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md max-h-[80vh] bg-black/95 border-white/20 [&_*]:text-white">
          <DialogHeader>
            <DialogTitle className="text-white font-poppins">
              {t("select_country") || "Select Country"}
            </DialogTitle>
          </DialogHeader>
          
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
            <Input
              type="text"
              placeholder={t("search") || "Search countries..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-black/50 border-white/20 text-white placeholder:text-white/50"
            />
          </div>

          {/* Countries List */}
          <ScrollArea className="h-[400px] mt-4">
            <div className="space-y-1 [&_button]:text-white">
              {filteredCountries.map((country: CountryWithCode) => {
                const phoneCode = country.phoneCode || getPhoneCode(country);
                const isSelected = selectedCountry?.name.common === country.name.common;
                
                return (
                  <button
                    key={country.name.common}
                    type="button"
                    onClick={() => handleSelectCountry(country)}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 transition-colors text-left text-white",
                      isSelected && "bg-primary/20 border border-primary/30"
                    )}
                    style={{ color: 'white' }}
                  >
                    {country.flags?.png && (
                      <Image
                        src={country.flags.png}
                        alt={country.name.common}
                        width={24}
                        height={18}
                        className="rounded-sm object-cover"
                      />
                    )}
                    <span className="flex-1 text-white font-poppins transition-colors" style={{ color: 'white' }}>
                      {country.name.common}
                    </span>
                    <span className="text-white font-medium font-poppins transition-colors" style={{ color: 'white' }}>
                      {phoneCode}
                    </span>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

